import { ProductClass } from "../types/enums";
import type { YearRange } from "../hooks/useDataAvailability";

/**
 * Metadata for a product classification
 */
export interface ClassificationConfig {
  value: ProductClass;
  label: string;
  displayName: string;
  yearRange: YearRange | null;
  supportsAllProductLevels: boolean;
  queryFieldName: string; // e.g., "productHs92", "productSitc"
}

/**
 * Generate a user-friendly display name from a classification code
 * Examples:
 *   "HS92" -> "HS 1992"
 *   "HS12" -> "HS 2012"
 *   "HS22" -> "HS 2022"
 *   "SITC" -> "SITC rev.2"
 */
export function getClassificationDisplayName(
  classification: ProductClass,
): string {
  const classStr = classification as string;

  // Handle older HS92 specifically (1992 not 2092)
  // This must come BEFORE the general HS pattern check
  if (classStr === "HS92") {
    return "HS 1992";
  }

  // Handle HS classifications with year
  // HS12 -> HS 2012, HS22 -> HS 2022, HS27 -> HS 2027, etc.
  if (classStr.startsWith("HS") && classStr.length === 4) {
    const year = classStr.substring(2);
    return `HS 20${year}`;
  }

  // Handle SITC
  if (classStr === "SITC") {
    return "SITC rev.2";
  }

  // Default: return as-is
  return classStr;
}

/**
 * Determine the GraphQL query field name for a classification
 * Examples:
 *   "HS92" -> "productHs92"
 *   "HS12" -> "productHs12"
 *   "SITC" -> "productSitc"
 */
export function getQueryFieldName(classification: ProductClass): string {
  const classStr = classification as string;

  // Convert to camelCase for GraphQL field name
  // HS92 -> productHs92
  // SITC -> productSitc
  return `product${classStr.charAt(0)}${classStr.substring(1).toLowerCase()}`;
}

/**
 * Check if a classification supports all product levels (1, 2, 4, 6)
 * Currently, SITC only supports levels 1, 2, 4 (no 6-digit)
 */
export function supportsAllProductLevels(
  classification: ProductClass,
): boolean {
  const classStr = classification as string;

  // SITC doesn't support 6-digit products
  if (classStr === "SITC") {
    return false;
  }

  // Default: support all levels
  return true;
}
